import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import isEmpty from 'ramda/src/isEmpty'
import cn from 'classnames'
import LoginLink from '../pageParts/LoginLink/LoginLink'
import { UPGRADE_TIER_PATH } from '../../constants/routes'
import { confirmEmail } from '../api'
import { Message, Spinner } from '../../ui'
import { getTextByLang } from '../ExternalFields'
import { useStore } from '../../StateProvider'
import formStyles from '../../ui/Form/style.css'
import { ErrorMessage } from '../ErrorMessage/ErrorMessage'
import { TARGET_PLATFORMS } from '../../constants/constants'
import { tByPlatform } from '../../i18n/i18nUtils'
import styles from './style.css'
import { pushEvent } from '../utils/googleTag'

const Tier1CompleteRegister = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [
    {
      whitelabel: {
        immediateLoginEnabled,
        targetPlatform,
        moveToUpgradeForm,
        customTexts,
        whitelabelDemoMode,
        googleTagType,
        accountCreationTier
      }
    }
  ] = useStore()
  const [user, setUser] = useState({})
  const [error, setError] = useState({})

  useEffect(() => {
    const { token, notificationToken } = queryString.parse(location.search)
    confirmEmail(token, notificationToken).then(
      /**
       * @param {Object} user
       */
      (user) => setUser(user),
      /**
       * @param {ErrorCommon} error
       */
      (error) => setError(error)
    )
  }, [])

  const isSpecialPlatform = targetPlatform === TARGET_PLATFORMS.MT4 ||
    targetPlatform === TARGET_PLATFORMS.MT5 ||
    targetPlatform === TARGET_PLATFORMS.IMAIBO ||
    targetPlatform === TARGET_PLATFORMS.MKTD ||
    (targetPlatform === TARGET_PLATFORMS.WT && accountCreationTier === 2)

  const hideForBP = targetPlatform === TARGET_PLATFORMS.BP && i18n.language === 'zh'

  let tier1CompleteTitle = getTextByLang('tier1CompleteTitle', customTexts)
  if (!tier1CompleteTitle) {
    tier1CompleteTitle = isSpecialPlatform
      ? t('tier1Complete.mt4CompletedTitle')
      : t(tByPlatform(targetPlatform, 'tier1Complete.completedTitle'))
  }
  let tier1CompleteButton = getTextByLang('tier1CompleteButton', customTexts)
  if (!tier1CompleteButton) {
    tier1CompleteButton = isSpecialPlatform ? t('tier1Complete.continue') : t('tier1Complete.increase')
  }
  if (isEmpty(user) && isEmpty(error)) {
    return <Spinner />
  } else if (!isEmpty(user) && moveToUpgradeForm) {
    return <Redirect to={{ pathname: UPGRADE_TIER_PATH }} />
  } else if (!isEmpty(user) && !immediateLoginEnabled) {
    return (
      <SuccessMessage
        title={tier1CompleteTitle}
        user={user}
        isSpecialPlatform={isSpecialPlatform}
        hideForBP={hideForBP}
        button={tier1CompleteButton}
        whitelabelDemoMode={whitelabelDemoMode}
        googleTagType={googleTagType}
      />
    )
  } else if (!isEmpty(user) && immediateLoginEnabled) {
    window.location.href = user.loginUrl
    return <Spinner />
  }
  return <ErrorMessage error={error} />
}

export const SuccessMessage = ({
  title,
  user,
  isSpecialPlatform,
  button,
  whitelabelDemoMode,
  hideForBP,
  googleTagType
}) => {
  const { t, i18n } = useTranslation()
  const history = useHistory()

  useEffect(() => {
    pushEvent('emailVerified', {
      formType: googleTagType,
      formCategory: user.ctierName,
      formCountry: user.residenceCountry,
      formCurrency: user.depositCurrency
    })
  }, [])

  return (
    <Message title={title} type='success'>
      {!isSpecialPlatform && !hideForBP && <span id='text'>{t('tier1Complete.completedText')}</span>}
      {whitelabelDemoMode && user.mtLogin?.length && user.mtServerName?.length && (
        <>
          <p className={cn(styles.additionalInfo,
            { [styles.reverseDirection]: i18n.language === 'ar' })}
          >
            {t('tier1Complete.login')}: {user.mtLogin}
          </p>
          {user.password?.length && (
            <p className={cn(styles.additionalInfo,
              { [styles.reverseDirection]: i18n.language === 'ar' })}
            >
              {t('tier1Complete.password')}: {user.password}
            </p>
          )}

          <p className={cn(styles.additionalInfo,
            { [styles.reverseDirection]: i18n.language === 'ar' })}
          >
            {t('tier1Complete.server')}: {user.mtServerName}
          </p>
        </>
      )}
      <span className={styles.buttonsBlock}>
        {!whitelabelDemoMode && !hideForBP && (
          <button
            className={`${formStyles.action} ${styles.increase}`}
            onClick={() => {
              history.replace(UPGRADE_TIER_PATH)
            }}
          >
            {button}
          </button>
        )}
        {!isSpecialPlatform && !whitelabelDemoMode && (
          <LoginLink
            text='tier1Complete.goToLogin'
            className={`${formStyles.action} ${styles.goToLogin}`}
            loginURL={user.loginUrl}
          />
        )}
      </span>
    </Message>
  )
}

export { Tier1CompleteRegister }
